import React, { forwardRef } from 'react';
import classnames from 'classnames';

import {
  FormControl,
  Typography,
  FormHelperText,
  FilledInput,
  InputAdornment,
  IconButton
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Icon, Button } from '@hydra/common/components';

import SubFolderSelection from '@hydra/common/components/GenBar/SubFolderSelection';

const useStyles = makeStyles(theme => ({
  inputWrapper: {
    flexDirection: 'column',
    alignItems: 'flex-start'
  },
  formWrapper: {
    display: 'flex',
    width: '100%',
    flexGrow: 1,
    flexDirection: 'row'
  },
  helperText: {
    fontWeight: theme.typography.fontWeightSemibold,
    margin: theme.spacing(2, 1, 1)
  },
  root: {
    fontSize: theme.typography.fontSizeLarge,
    paddingLeft: `${theme.spacing(2)}px`,
    marginRight: theme.spacing(2),
    boxShadow: theme.shadows[3],
    background: theme.palette.background.paper,
    '&:hover': {
      background: theme.palette.background.paper,
      boxShadow: theme.shadows[4]
    },
    '&.Mui-focused': {
      background: theme.palette.background.paper,
      boxShadow: theme.shadows[5]
    },
    [theme.breakpoints.down('xs')]: {
      marginRight: 0,
      paddingLeft: theme.spacing(1)
    }
  },
  input: {
    fontSize: theme.typography.fontSizeLarge,
    padding: `${theme.spacing(2)}px ${theme.spacing(0.5)}px`
  },
  startAdornment: {
    fontSize: theme.typography.fontSizeLarge,
    fontWeight: theme.typography.fontWeightSemibold,
    color: theme.palette.grey[500]
  },
  endAdornment: {
    display: 'inline-flex'
  },
  btn: {
    height: 'auto', // compensate fixed button height
    maxWidth: 170,
    boxShadow: theme.shadows[5],
    flexShrink: 0,
    '&.Mui-disabled': {
      boxShadow: theme.shadows[3]
    },
    [theme.breakpoints.down('xs')]: {
      display: 'none'
    }
  },
  btnLabel: {
    fontWeight: theme.typography.fontWeightSemibold
  },
  loader: {
    verticalAlign: 'middle'
  }
}));

const EndAdornment = ({ handleClear }: { handleClear: () => void }) => {
  const classes = useStyles();
  return (
    <InputAdornment position='end' className={classes.endAdornment}>
      <IconButton size='small' onClick={handleClear}>
        <Icon type='cross' size='sm' color='secondary' />
      </IconButton>
    </InputAdornment>
  );
};

const StartAdornment = ({ url }: { url: string }) => {
  const classes = useStyles();
  return (
    <InputAdornment
      position='start'
      className={classes.startAdornment}
    >
      {url}/
    </InputAdornment>
  );
};

interface GenBarProps {
  handleSubmit: () => void,
  handleChange: (e: any) => void,
  handleSubFolderChange: (e: any) => void,
  handleClear: () => void,
  disabled: boolean,
  isLoading: boolean,
  placeholder: string | null,
  subFolderPlaceholder: string | null,
  buttonText?: string,
  homepageUrl?: string,
  handleKeyDown: (e: any) => void,
  value: string,
  subFolderValue?: string,
  allowSubFolder?: boolean | string,
  error?: boolean,
  helperText: string,
  className?: string,
  id: string
}

const GenBar = forwardRef((props: GenBarProps, ref: React.ForwardedRef<any>) => {
  const classes = useStyles();

  const {
    handleSubmit: handleSubmitProp,
    handleChange,
    handleSubFolderChange,
    handleClear,
    disabled,
    isLoading,
    placeholder,
    subFolderPlaceholder,
    buttonText,
    homepageUrl,
    handleKeyDown,
    value,
    subFolderValue,
    allowSubFolder = false,
    error = false,
    helperText,
    className: classNameProp,
    id
  } = props;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    try {
      e.preventDefault();
      handleSubmitProp();
    } catch (error: any) {
      throw new Error(error);
    }
  };

  return (
    <>
      <FormControl
        fullWidth
        className={classnames(
          classNameProp,
          classes.inputWrapper
        )}
        component='form'
        onSubmit={handleSubmit}
      >
        <div className={classes.formWrapper}>
          <FilledInput
            disabled={disabled}
            error={error}
            inputRef={ref}
            autoFocus
            inputProps={{
              className: 'nr-generation-form-input',
              autoComplete: 'off'
            }}
            fullWidth
            placeholder={placeholder || ''}
            disableUnderline
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            classes={{
              root: classes.root,
              input: classes.input
            }}
            value={value}
            endAdornment={value && !disabled && <EndAdornment handleClear={handleClear} />}
            startAdornment={homepageUrl && <StartAdornment url={homepageUrl} />}
            {...id && { id }}
          />

          <Button
            className='nr-generation-form-button'
            variant='contained'
            color='primary'
            disabled={!value}
            classes={{ root: classes.btn }}
            type='submit'
          >
            {
              isLoading
                ? (
                  <Icon type='loader' component='span' color='white' className={classes.loader} />
                  )
                : (
                  <Typography noWrap>
                    <span className={classes.btnLabel}>{buttonText}</span>
                  </Typography>
                  )
            }
          </Button>
        </div>
        <div>
          {
            error && helperText && (
              <FormHelperText
                classes={{ root: classes.helperText }}
                error={error}
              >
                {helperText}
              </FormHelperText>
            )
          }
        </div>
      </FormControl>
      {
        allowSubFolder
          ? (
            <SubFolderSelection
              subFolderPlaceholder={subFolderPlaceholder || ''}
              disabled={disabled}
              error={error}
              handleSubFolderChange={handleSubFolderChange}
              handleKeyDown={handleKeyDown}
              classes={classes}
              id={id}
              subFolderValue={subFolderValue}
            />
            )
          : null
      }
    </>
  );
});

export default GenBar;
